import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useGetMeQuery, useGetOpenWearablesUsersQuery } from '@@/services/user';
import { Roles } from '@@/constants/user';

type OpenWearablesUserRow = Pick<
  Unpatient.User,
  'id' | 'firstName' | 'lastName' | 'openWearablesId'
>;

const OPEN_WEARABLES_WEB_URL = import.meta.env.VITE_OPEN_WEARABLES_WEB_URL;

const columnHelper = createColumnHelper<OpenWearablesUserRow>();

const OpenWearablesUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: me } = useGetMeQuery();

  // Page is ADMIN/PRACTITIONER-only. The /admin/* routes are auth-guarded but
  // not role-guarded, so anyone else who types the URL is redirected away.
  useEffect(() => {
    if (me && me.role !== Roles.ADMIN && me.role !== Roles.PRACTITIONER) {
      navigate('/admin');
    }
  }, [me, navigate]);

  const { data: users, isLoading, isError } = useGetOpenWearablesUsersQuery();

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => `${row.firstName} ${row.lastName}`, {
        id: 'fullName',
        header: () => <p className="mx-2 text-xs sm:text-sm">Nom</p>,
        cell: (info) => <p className="mx-2">{info.getValue()}</p>,
      }),
      columnHelper.accessor('openWearablesId', {
        id: 'openWearablesId',
        header: () => (
          <p className="mx-2 text-xs sm:text-sm">ID OpenWearables</p>
        ),
        cell: (info) => {
          const openWearablesId = info.getValue();
          if (!openWearablesId) {
            return null;
          }
          return (
            <a
              href={`${OPEN_WEARABLES_WEB_URL}/users/${openWearablesId}`}
              target="_blank"
              rel="noreferrer"
              className="mx-2 text-cyan-600 underline"
            >
              {openWearablesId}
            </a>
          );
        },
      }),
    ],
    [],
  );

  const data = useMemo(() => users ?? [], [users]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 20 } },
  });

  if (isLoading) {
    return <div className="p-4 w-full">Chargement…</div>;
  }

  if (isError) {
    return (
      <div className="p-4 w-full">
        Une erreur est survenue lors du chargement des utilisateurs.
      </div>
    );
  }

  if (data.length === 0) {
    return <div className="p-4 w-full">Aucun utilisateur OpenWearables.</div>;
  }

  return (
    <div className="p-4 w-full">
      <h1 className="text-xl font-semibold mb-4">Utilisateurs OpenWearables</h1>

      <table className="my-8">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="text-center mx-2">
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="text-center mx-2">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex flex-row items-center gap-2">
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          className="border rounded p-1 px-2 disabled:opacity-40"
        >
          Précédent
        </button>
        <span className="text-sm">
          Page {table.getState().pagination.pageIndex + 1} sur{' '}
          {table.getPageCount()}
        </span>
        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          className="border rounded p-1 px-2 disabled:opacity-40"
        >
          Suivant
        </button>
      </div>
    </div>
  );
};

export default OpenWearablesUsersPage;
